import { useMemo } from "react";
import { classNames } from "primereact/utils";
import styles from "./TimeSlots.module.scss";
import { IAvailableTimeSlot, SlotStatus } from "../../../models";
import { isWaitlistStatus, isBlockedStatus } from "../../../../../../common/utilities/slotStatusUtility";
import { useAppContext } from "../../../../../../AppContext";

interface ITimeSlotsProps {
  availableSlots: IAvailableTimeSlot[];
  selectedTime?: string | null;
  selectedEventId?: number | null;
  onTimeSelect: (time: string, eventId?: number) => void;
  selectedDate?: Date | null;
  className?: string;
}

// Utility function to format available spaces label
const getAvailableSpacesLabel = (
  availableSpaces?: number,
  slotStatus?: SlotStatus,
): string | null => {
  if (availableSpaces === undefined) {
    return null;
  }

  // Waitlist only
  if (isWaitlistStatus(slotStatus)) {
    return "Списък на чакащите";
  }

  // Blocked
  if (isBlockedStatus(slotStatus)) {
    return null;
  }

  // Singular form
  if (availableSpaces === 1) {
    return "1 място";
  }

  // Plural form with counter
  if (availableSpaces > 1) {
    return `${availableSpaces} места`;
  }

  // No spaces
  return null;
};

export const TimeSlots = ({
  availableSlots,
  selectedTime,
  selectedEventId,
  onTimeSelect,
  selectedDate,
  className,
}: ITimeSlotsProps) => {
  const { selectedStudio } = useAppContext();
  const showAvailableSpaces =
    selectedStudio?.showAvailableSeatsInPortal ?? false;

  // Filter and sort available slots
  const sortedSlots = useMemo(() => {
    const now = new Date();
    const isToday =
      selectedDate && selectedDate.toDateString() === now.toDateString();

    return availableSlots
      .filter((slot) => {
        // Show all slots, even unavailable ones (for existing reservations)
        // Only filter out past time slots if it's today
        if (isToday) {
          const [hours, minutes] = slot.time.split(":").map(Number);
          const slotTime = new Date();
          slotTime.setHours(hours, minutes, 0, 0);

          // Only show slots that are in the future
          return slotTime > now;
        }

        return true;
      })
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [availableSlots, selectedDate]);

  const handleSlotClick = (slot: IAvailableTimeSlot) => {
    if (slot.isAvailable) {
      onTimeSelect(slot.time, slot.eventId);
    }
  };

  const isSlotSelected = (slot: IAvailableTimeSlot): boolean => {
    if (selectedTime !== slot.time) return false;
    if (selectedEventId != null && slot.eventId != null) return selectedEventId === slot.eventId;
    return true;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString("bg-BG", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  if (sortedSlots.length === 0) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h3 className={styles.title}>Свободни часове</h3>
          {selectedDate && (
            <p className={styles.dateLabel}>за {formatDate(selectedDate)}</p>
          )}
        </div>
        <div className={styles.noSlotsMessage}>
          <div className={styles.noSlotsIcon}>⏰</div>
          <p className={styles.noSlotsText}>
            Няма свободни часове за избраната дата
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.header}>
        <h3 className={styles.title}>Свободни часове</h3>
        {selectedDate && (
          <p className={styles.dateLabel}>за {formatDate(selectedDate)}</p>
        )}
      </div>

      <div className={styles.slotsGrid}>
        {sortedSlots.map((slot, index) => {
          const slotId = `time-slot-${slot.time.replace(":", "-")}-${slot.employeeId || "no-employee"}`;
          const isWaitlist = isWaitlistStatus(slot.slotStatus);
          const isBlocked = isBlockedStatus(slot.slotStatus);
          const isCancelled = !!slot.isCancelled;
          // Only apply blocked styling for reservation closure, not for "already have
          // reservation" and not for cancelled classes (which get their own styling).
          const isReservationClosed =
            isBlocked &&
            !isCancelled &&
            slot.disabledReason !== "Вече имате резервация за този час";
          const statusText = isWaitlist
            ? "Списък изчакващи"
            : !slot.isAvailable && slot.disabledReason
              ? slot.disabledReason
              : null;
          const spacesLabel = showAvailableSpaces
            ? getAvailableSpacesLabel(slot.availableSpaces, slot.slotStatus)
            : null;

          return (
            <div key={slotId}>
              <div
                id={slotId}
                className={classNames(styles.timeSlot, {
                  [styles.selected]: isSlotSelected(slot),
                  [styles.disabled]: !slot.isAvailable && !isCancelled,
                  [styles.waitlist]: isWaitlist && slot.isAvailable,
                  [styles.blocked]: isReservationClosed,
                  [styles.cancelled]: isCancelled,
                })}
                onClick={() => handleSlotClick(slot)}
                style={{ animationDelay: `${index * 50}ms` }}
                role="button"
                tabIndex={slot.isAvailable ? 0 : -1}
                onKeyDown={(e) => {
                  if (
                    (e.key === "Enter" || e.key === " ") &&
                    slot.isAvailable
                  ) {
                    e.preventDefault();
                    handleSlotClick(slot);
                  }
                }}
              >
                <div className={styles.slotContent}>
                  <span className={styles.timeText}>{slot.time}</span>
                  {spacesLabel && (
                    <span className={styles.spacesLabel}>{spacesLabel}</span>
                  )}
                  {statusText && (
                    <span className={styles.statusLabel}>{statusText}</span>
                  )}
                  {slot.isAvailable && !isWaitlist && (
                    <div className={styles.availableIndicator}>
                      <div className={styles.availableDot}></div>
                    </div>
                  )}
                  {isWaitlist && slot.isAvailable && (
                    <div className={styles.waitlistIndicator}>
                      <span className={styles.waitlistIcon}>⏳</span>
                    </div>
                  )}
                </div>

                {isSlotSelected(slot) && (
                  <div className={styles.selectedIndicator}>
                    <span className={styles.checkmark}>✓</span>
                  </div>
                )}

                <div className={styles.hoverOverlay}></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.footer}>
        <div className={styles.slotCount}>
          {sortedSlots.length} свободни слота
        </div>
        {selectedTime && (
          <div className={styles.selectedTimeDisplay}>
            Избран час:{" "}
            <span className={styles.selectedTimeValue}>{selectedTime}</span>
          </div>
        )}
      </div>
    </div>
  );
};
